import {
    ChangeEvent,
    FormEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormFeedback,
} from "reactstrap";

import styles from "Pages/Admin/AdminConfiguration/AdminConfigModal/AdminConfigModal.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import Slider from "components/Slider/Slider";

type ActionType = "new" | "edit" | "delete" | "";

interface IProps {
    activeModal: ActionType;
    getAdminConfigs: (ignore: boolean) => Promise<void>;
    onSetActiveModal: (modal: ActionType) => void;
    onSetSelectedRow: (row: any) => void;
    selectedRow: any;
}

interface ICompanies {
    company_id: string;
    name: string;
}

const DATA_TYPE_INPUT_OPTIONS = ["text", "list", "email_list", "true/false"];

export default function AdminConfigModal({
    activeModal,
    getAdminConfigs,
    onSetActiveModal,
    onSetSelectedRow,
    selectedRow,
}: IProps) {
    const [formData, setFormData] = useState({
        company_id: "",
        purpose: "",
        key: "",
        value: "",
        data_type: "",
        id: 0,
        is_obscured: false,
    });
    const [companies, setCompanies] = useState<ICompanies[]>([]);
    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();

    const { handleFormChange, isValidEmail } = useHelpers();
    const { get, post } = useFetch();

    const getCompanies = useCallback(async () => {
        const response = await get(`/api/admin/company`);

        let data: Response | ICompanies[];
        try {
            data = await response.json();
        } catch (e) {
            data = response;
        }
        if (response.ok) {
            data = data as ICompanies[];
            setCompanies(data);
        }
    }, [get]);

    const closeModal = useCallback(() => {
        onSetActiveModal("");
        setFormData({
            company_id: "",
            purpose: "",
            key: "",
            value: "",
            data_type: "",
            id: 0,
            is_obscured: false,
        });
    }, [onSetActiveModal]);

    // Handle alert for no row selected, prefilling inputs conditionally
    useEffect(() => {
        getCompanies();
        if (activeModal === "new")
            setFormData({
                company_id: "",
                purpose: "",
                key: "",
                value: "",
                data_type: "",
                id: 0,
                is_obscured: false,
            });

        // Handle no row selected for edit and delete
        if (
            (activeModal === "edit" || activeModal === "delete") &&
            !selectedRow
        ) {
            closeModal();
            newAlert({
                message: "No entry selected",
                color: "danger",
            });
            return;
        }

        // Set formData to selected row values so values appear in inputs
        if (activeModal !== "new") {
            setFormData({
                company_id: selectedRow ? selectedRow.values.company_id : "",
                purpose: selectedRow ? selectedRow.values.purpose : "",
                key: selectedRow ? selectedRow.values.keyName : "",
                value: selectedRow ? selectedRow.values.keyValue : "",
                data_type: selectedRow ? selectedRow.values.data_type : "",
                id: selectedRow ? selectedRow.values.id : 0,
                is_obscured: selectedRow
                    ? selectedRow.values.is_obscured
                    : false,
            });
        }
    }, [activeModal, closeModal, getCompanies, newAlert, selectedRow]);

    async function submitFormData(e: FormEvent) {
        e.preventDefault();

        const targetCompanyId = formData.company_id?.trim() || userContext.companyId;
        const url = `/api/admin/config/${activeModal}/${targetCompanyId}`;

        const body = {
            company_id: formData.company_id,
            purpose: formData.purpose,
            keyName: formData.key.trim(),
            keyValue: formData.value.trim(),
            data_type: formData.data_type,
            is_obscured: formData.is_obscured,
            id: formData.id,
        };

        const response = await post(url, body);
        const data = await response.json();
        console.log(data);

        // If the setting already exists, stop
        if (
            data.detail === "Setting already exists." ||
            data.detail ===
                "Failed to edit configuration: Sequence contains more than one element"
        ) {
            var footer = document.getElementById("modal_footer")!.innerHTML;
            if (!footer.includes("Key already exists."))
                document.getElementById("modal_footer")!.innerHTML =
                    "Key already exists." + footer;
        }
        // Otherwise proceed as normal
        else {
            closeModal();

            if (response.ok) {
                // Show the alert
                newAlert({
                    isOpen: true,
                    message: "Request submitted successfully",
                    color: "success",
                });
                // Erase the selected row state
                onSetSelectedRow(undefined);
                // Fetch the company configurations again from the parent component to update the table
                getAdminConfigs(false);
            } else {
                newAlert({
                    isOpen: true,
                    message: `Error, request failed`,
                    color: "danger",
                });
            }
        }
    }

    let headerContent = "";
    switch (activeModal) {
        case "new":
            headerContent = "Create new entry";
            break;
        case "edit":
            headerContent = "Edit entry";
            break;
        case "delete":
            headerContent = "Delete entry";

            break;
    }

    // Updates values in each input of a list config
    const updateListValue = (
        e: ChangeEvent<HTMLInputElement>,
        entry: string,
        value_array: string[],
        index: number
    ) => {
        value_array[index] = entry;
        formData.value = value_array.join("|");
        handleFormChange(e, setFormData);
    };

    // Adds an input field to a list
    const addInput = (e: React.MouseEvent<HTMLButtonElement>) => {
        let new_value = formData.value + "|";
        setFormData({ ...formData, value: new_value });
    };

    // Removes an input field
    const removeInput = (
        e: React.MouseEvent<HTMLButtonElement>,
        index: number
    ) => {
        let value_array = formData.value.split("|");
        value_array.splice(index, 1);
        let new_value = value_array.join("|");
        setFormData({ ...formData, value: new_value });
    };

    // Changes whether a config is hidden or not
    const toggleHidden = (e: ChangeEvent<HTMLInputElement>) => {
        if (!formData.is_obscured) formData.is_obscured = true;
        else formData.is_obscured = false;
        // if (e.target.id === "obscured")
        //     formData.is_obscured = true;
        // else
        //     formData.is_obscured = false;
        handleFormChange(e, setFormData);
        console.log(formData.is_obscured);
    };

    const toggleTrueOrFalse = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.id === "true") formData.value = "true";
        else formData.value = "false";
        handleFormChange(e, setFormData);
        console.log(formData.value);
    };

    const isEmailList = (value: string) => {
        return value.split(",").every((e) => isValidEmail(e));
    };

    let defaultValueInput = (
        <FormGroup row className={styles.formGroup}>
            <Col sm={2}>
                <Label for="value">Value:</Label>
            </Col>
            <Col sm={10}>
                <Input
                    id="value"
                    name="value"
                    className={styles.input}
                    minLength={1}
                    maxLength={4096}
                    required
                    onChange={(e) => handleFormChange(e, setFormData)}
                    value={formData.value}
                    disabled={activeModal === "delete"}
                    invalid={
                        formData.value.length >= 3 &&
                        formData.data_type === "email_list" &&
                        !isEmailList(formData.value)
                    }
                />
                <FormFeedback>
                    Please enter a list of valid emails separated by commas
                </FormFeedback>
            </Col>
        </FormGroup>
    );

    // Input fields for "list" type configs
    if (formData.data_type === "list") {
        let value_array = formData.value.split("|");
        defaultValueInput = (
            <div>
                {value_array.map((entry, index) => (
                    <FormGroup key={entry} row className={styles.formGroup}>
                        <Col sm={2}>
                            <Label for="value">Value {index + 1}:</Label>
                        </Col>
                        <Col sm={9}>
                            <Input
                                id="entry"
                                name="entry"
                                className={styles.input}
                                minLength={1}
                                maxLength={4096}
                                required
                                onChange={(e) =>
                                    updateListValue(
                                        e,
                                        e.target.value,
                                        value_array,
                                        index
                                    )
                                }
                                value={formData.value.split("|")[index]}
                                disabled={activeModal === "delete"}
                            />
                        </Col>
                        <Button
                            sm={2}
                            color="danger"
                            style={{ width: "30px" }}
                            onClick={(e) => removeInput(e, index)}
                        >
                            X
                        </Button>
                    </FormGroup>
                ))}
                <FormGroup row className={styles.formGroup}>
                    <Button
                        onClick={(e) => addInput(e)}
                        type="button"
                        style={{ width: "85px" }}
                    >
                        Add value
                    </Button>
                </FormGroup>
                <FormGroup row className={styles.formGroup} hidden={true}>
                    <Col sm={2}>
                        <Label for="entry">List:</Label>
                    </Col>
                    <Col sm={10}>
                        <Input
                            id="value"
                            name="value"
                            className={styles.input}
                            minLength={1}
                            maxLength={4096}
                            required
                            onChange={(e) => handleFormChange(e, setFormData)}
                            value={formData.value}
                            disabled={
                                activeModal === "new" ||
                                activeModal === "edit" ||
                                activeModal === "delete"
                            }
                        />
                    </Col>
                </FormGroup>
            </div>
        );
    } else if (formData.data_type === "true/false") {
        defaultValueInput = (
            <div>
                <FormGroup row className={styles.formGroup}>
                    <Col sm={2}>
                        <Label for="value">Value:</Label>
                    </Col>
                    <Col sm={2}>
                        <Input
                            sm={2}
                            id="true"
                            name="true"
                            className={styles.input}
                            type="radio"
                            minLength={1}
                            maxLength={4096}
                            onChange={(e) => toggleTrueOrFalse(e)}
                            checked={formData.value === "true"}
                            disabled={activeModal === "delete"}
                        />
                        <Label sm={1}>True</Label>
                    </Col>
                    <Col sm={1}>
                        <Input
                            id="false"
                            name="false"
                            className={styles.input}
                            type="radio"
                            minLength={1}
                            maxLength={4096}
                            onChange={(e) => toggleTrueOrFalse(e)}
                            checked={formData.value === "false"}
                            disabled={activeModal === "delete"}
                        />
                        <Label sm={1}>False</Label>
                    </Col>
                </FormGroup>
            </div>
        );
    }

    const bodyContent = (
        <Form id="form" onSubmit={submitFormData}>
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="company_id">Company ID:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="company_id"
                        name="company_id"
                        className={styles.input}
                        type="select"
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.company_id}
                        disabled={activeModal === "delete"}
                    >
                        <option>Choose a company</option>
                        {companies.map((company) => (
                            <option
                                key={company.name}
                                value={company.company_id}
                            >
                                {company.company_id}
                                {company.company_id.toLowerCase() !==
                                    company.name.toLowerCase() &&
                                    ` - ${company.name}`}
                            </option>
                        ))}
                    </Input>
                </Col>
            </FormGroup>
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="purpose">Purpose:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="purpose"
                        name="purpose"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.purpose}
                        disabled={activeModal === "delete"}
                    />
                </Col>
            </FormGroup>
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="key">Key: </Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="key"
                        name="key"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.key}
                        disabled={activeModal === "delete"}
                        invalid={
                            formData.value.length >= 3
                                ? formData.company_id !== "" &&
                                  !formData.key
                                      .toLowerCase()
                                      .startsWith(
                                          `${formData.company_id.toLowerCase()}.`
                                      )
                                : false
                        }
                    />
                    <FormFeedback>
                        A company id is set. Key values should most likely start
                        with company_id.
                    </FormFeedback>
                </Col>
            </FormGroup>
            {defaultValueInput}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="data_type">Data Type:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        id="data_type"
                        name="data_type"
                        type="select"
                        className={styles.input}
                        minLength={1}
                        maxLength={4096}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.data_type}
                        disabled={activeModal === "delete"}
                    >
                        <option>Choose a data type</option>
                        {DATA_TYPE_INPUT_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Input>
                </Col>
            </FormGroup>
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label for="is_obscured">Hidden:</Label>
                </Col>
                <Col sm={2}>
                    <Slider
                        checked={formData.is_obscured}
                        onChange={(e) => toggleHidden(e)}
                        size={0.5}
                    />
                </Col>
            </FormGroup>
            <Input
                type="hidden"
                id="id"
                name="id"
                className={styles.input}
                minLength={1}
                maxLength={4096}
                required
                onChange={(e) => handleFormChange(e, setFormData)}
                value={formData.id}
                disabled={
                    activeModal === "new" ||
                    activeModal === "edit" ||
                    activeModal === "delete"
                }
            />
        </Form>
    );

    return (
        <Modal isOpen={activeModal !== ""} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>{headerContent}</ModalHeader>
            <ModalBody>{bodyContent}</ModalBody>
            <ModalFooter id="modal_footer">
                <Button color="primary" type="submit" form="form">
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    );
}
